import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Smartphone, Laptop, Tv, Refrigerator, Gamepad2, Shirt, BookOpen, Home as HomeIcon, Dumbbell, Baby, ShieldCheck } from 'lucide-react';
import { Card } from './ui/card';
import { categoriesAPI } from '../services/api';
import MegaMenuPanel from './MegaMenuPanel';

const CategorySidebar = () => {
  const [categories, setCategories] = useState([]);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const categoryRefs = useRef({});
  const cardRef = useRef(null);
  const leaveTimeoutRef = useRef(null);
  const location = useLocation();
  
  const iconMap = {
    'Smartphone': Smartphone,
    'Laptop': Laptop,
    'Tv': Tv,
    'Refrigerator': Refrigerator,
    'Gamepad2': Gamepad2,
    'Shirt': Shirt,
    'BookOpen': BookOpen,
    'Home': HomeIcon,
    'Dumbbell': Dumbbell,
    'Baby': Baby,
  };

  useEffect(() => {
    loadCategories();
    
    // Cleanup timeout on unmount
    return () => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  // Reset hover state when location changes (navigation between pages)
  useEffect(() => {
    setHoveredCategory(null);
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, [location.pathname]);

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      // Filter only active categories
      const activeCategories = (response.data || []).filter(cat => cat.isActive !== false);
      setCategories(activeCategories);
      // Reset refs when categories change
      categoryRefs.current = {};
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Get only main categories (no parentId), sorted by order
  const mainCategories = categories
    .filter(cat => !cat.parentId)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  
  // Get subcategories for a parent
  const getSubcategories = (parentId) => {
    return categories
      .filter(cat => cat.parentId === parentId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const handleCategoryHover = (categoryId) => {
    // Clear any pending leave timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    
    setHoveredCategory(categoryId);
    
    // eMAG-style: panel is aligned with the TOP of the whole menu card and spans full height
    const element = categoryRefs.current[categoryId];
    if (element) {
      const rect = element.getBoundingClientRect();
      const cardRect = cardRef.current ? cardRef.current.getBoundingClientRect() : rect;
      setPanelPosition({
        anchor: { top: rect.top, bottom: rect.bottom, right: rect.right },
        cardTop: cardRect.top
      });
    }
  };
  
  const handleCategoryLeave = () => {
    // Add a delay before hiding the panel
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredCategory(null);
    }, 150); // 150ms delay
  };
  
  const handlePanelEnter = () => {
    // Clear the leave timeout when entering the panel
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  };
  
  const handlePanelLeave = () => {
    // Hide panel immediately when leaving it
    setHoveredCategory(null);
  };

  return (
    <>
      <Card ref={cardRef} className="rounded-2xl border-2 border-gray-100 overflow-hidden sticky top-24">
        <div className="bg-green-600 text-white p-4 font-bold text-lg">
          Toate Categoriile
        </div>
        <nav className="divide-y divide-gray-100 relative" style={{ overflow: 'visible' }}>
          <Link
            to="/rca"
            className="flex items-center justify-between p-4 bg-green-50/60 hover:bg-green-100 transition-colors group"
            data-testid="sidebar-rca-link"
          >
            <div className="flex items-center space-x-3">
              <ShieldCheck className="h-5 w-5 text-green-600" />
              <span className="text-sm font-semibold text-green-800">RCA · Asigurări</span>
            </div>
            <ChevronRight className="h-4 w-4 text-green-600" />
          </Link>
          {mainCategories.map((category, index) => {
            const Icon = iconMap[category.icon] || Smartphone;
            const subcategories = getSubcategories(category._id);
            const hasSubcategories = subcategories.length > 0;
            
            return (
              <div
                key={category._id}
                ref={(el) => categoryRefs.current[category._id] = el}
                onMouseEnter={() => handleCategoryHover(category._id)}
                onMouseLeave={handleCategoryLeave}
                className="relative"
                data-testid={`sidebar-cat-${category.slug}`}
              >
                <Link
                  to={`/catalog?category=${category.slug}`}
                  className="flex items-center justify-between p-4 hover:bg-green-50 transition-colors group relative"
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-green-600">
                      {category.name}
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-green-600" />
                </Link>
              </div>
            );
          })}
        </nav>
      </Card>
      
      {/* eMAG-style mega menu: full-height panel, L2 bold + L3 links (L4 only on selection) */}
      {hoveredCategory && (() => {
        const cat = mainCategories.find((c) => c._id === hoveredCategory);
        if (!cat) return null;
        return (
          <MegaMenuPanel
            category={cat}
            categories={categories}
            anchor={panelPosition.anchor}
            cardTop={panelPosition.cardTop}
            onEnter={handlePanelEnter}
            onLeave={handlePanelLeave}
          />
        );
      })()}
    </>
  );
};

export default CategorySidebar;
